// Single source of truth for the flags this repo checks.
// name    — snake_case, studentportal_-prefixed, matches the gate name in the Statsig console
//           (dst_changes is a deliberate exception — see its entry below)
// default — served when the provider is unreachable/not ready; also what the local provider serves.
//           Release flags default false; only an operational switch that must survive an outage ON
//           may declare default true.
export const FLAGS = {
  STUDENTPORTAL_FLAGS_CANARY: {
    name: "studentportal_flags_canary",
    default: false,
    description:
      "Feature-flag system canary — safe to flip in any tier; proves the local/Statsig wiring end to end",
  },
  // Intentionally unprefixed: ONE Statsig gate shared with Sandbox, so a single
  // flip switches DST-aware labels on in both apps at once. The two apps render
  // the same classes — showing one abbreviation in the Portal and a different
  // one in Sandbox would be worse than shipping neither. SPRIN-3287, already in
  // production on its own; the edit-class gate below does not change it.
  DST_CHANGES: {
    name: "dst_changes",
    default: false,
    description:
      "DST-aware timezone abbreviations — derive the abbreviation from the IANA zone at each class/slot instant instead of the hardcoded label; also replaces the reschedule timezone picker with per-slot abbreviations",
  },
  // SPRIN-3248 edit class, and what is folded into it: the reschedule/cancellation limits
  // (SPRIN-3248) and mandatory auto-substitution (SPRIN-3256). The revamped page labels its own
  // slots DST-correctly whatever dst_changes says; the legacy screens keep following dst_changes.
  // ONE gate for all of it, on this frontend and on both backends: StudentPortalApi and EpicenterApi
  // register this identical name, and all three read the same console gate in the same Statsig
  // project, so a single flip releases or rolls back the whole feature.
  //
  // ON: /cancel/reschedule/:studentId serves the revamped EditClass page, and the legacy reschedule
  // screens apply mandatory substitution. OFF: those screens are exactly the legacy behaviour.
  //
  // Roll out BY TIER ONLY, never by user list or percentage. The backends evaluate this gate on one
  // fixed system identity (their workers have no student), while this page evaluates it on the
  // student. Per-user targeting would let the page promise "1 cancellation will be used" while the
  // backend that charges it reads OFF — the exact bug QA reported, recreated by configuration.
  STUDENTPORTAL_EDIT_CLASS_REVAMP: {
    name: "studentportal_edit_class_revamp",
    default: false,
    description:
      "Edit Class (SPRIN-3248) with its limits ladder and mandatory substitution (SPRIN-3256); OFF is the legacy screens throughout",
  },
  // Intentionally unprefixed: ONE Statsig gate shared with Sandbox. The two
  // apps share one rotating token lineage through the shared cookies, and an
  // embedded Sandbox delegates its refresh to this app's coordinator — a
  // per-app gate would leave the same session recovering in one app and
  // logging out in the other.
  //
  // ON: before refreshing, and again when cy-sso rejects the refresh with a
  // 401, the API client looks for a same-user, strictly-newer token pair in
  // the sibling stores and adopts it instead of logging out. OFF: a tab whose
  // tokens were rotated away by the other app more than a minute ago (past
  // cy-sso's recentDeletion grace) logs out on its next request, and its
  // sessionExpired() cascade logs the other app out too.
  AUTH_STALE_TAB_RECOVERY: {
    name: "auth_stale_tab_recovery",
    default: false,
    description:
      "A tab holding tokens the other app already rotated adopts the newer pair from the shared stores instead of logging out; off = it logs out once the rotation is over a minute old",
  },
  // ON: the header "Switch Profile" modal switches to a sibling through cy-sso's
  // /api/login/student/sibling-auth (the call Sandbox's dashboard switch already
  // makes), so the new profile gets a cy-sso session. OFF: it calls the old
  // StudentPortalApi /switchProfile, whose token is not a cy-sso token, and the
  // next API request logs the student out (legacy-token).
  STUDENTPORTAL_SIBLING_SWITCH_CY_SSO: {
    name: "studentportal_sibling_switch_cy_sso",
    default: false,
    description:
      "Header Switch Profile goes through cy-sso sibling-auth; off = the old /switchProfile, which logs the student out on the next request",
  },
};

const byName = Object.values(FLAGS).reduce(
  (acc, flag) => ({ ...acc, [flag.name]: flag }),
  {},
);

export const defaultFor = (flagName) => byName[flagName]?.default ?? false;
